import React from "react";
import {
  BaseEntity,
  SimpleTableConfig,
  EntityHooks,
  TableFilters,
} from "@/components/shared/UniversalCRMTable/types/shared";
import { GroupedItem } from "@/lib/utils/crm/grouping-strategies";
import { GroupedListView } from "../GroupedListView";
import { HorizontalCardView } from "../UniversalCRMTable/HorizontalCardView";
import type { GroupingContext, GroupableField, GroupingWrapperProps } from "./types";
import type { GroupingViewMode } from "./useGroupingPageContext";
import type { AuthenticatedUser } from "@/lib/types/unified";

export interface GroupingWrapperRenderProps<T extends BaseEntity = BaseEntity> {
  processedData: (GroupedItem | T)[];
  groupingContext: GroupingContext;
  groupableFields: GroupableField[];
  isCardView?: boolean;
  hideTableBody?: boolean;
}

export interface GroupingWrapperContentProps<T extends BaseEntity> {
  viewMode: GroupingViewMode;
  BoardViewComponent: React.ComponentType | undefined;
  appUser: AuthenticatedUser | undefined;
  data: T[];
  config: SimpleTableConfig<T>;
  processedData: (GroupedItem | T)[];
  groupingContext: GroupingContext;
  groupableFields: GroupableField[];
  groupByField: string | null;
  dualGrouping: { primary: string | null; secondary: string | null };
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filters: TableFilters;
  setFilters: (filters: TableFilters) => void;
  onExport: (data: T[], format: string) => void;
  onEdit: (item: T) => void;
  onView: (item: T) => void;
  onOpenDetailModal?: (item: T, ids: string[], index: number) => void;
  hooks?: EntityHooks<T>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore?: () => void;
  children: GroupingWrapperProps<T>["children"];
}

export function GroupingWrapperContent<T extends BaseEntity>({
  viewMode,
  BoardViewComponent,
  appUser,
  data,
  config,
  processedData,
  groupingContext,
  groupableFields,
  groupByField,
  dualGrouping,
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
  onExport,
  onEdit,
  onView,
  onOpenDetailModal,
  hooks,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  children,
}: GroupingWrapperContentProps<T>) {
  const renderChildren = (props: GroupingWrapperRenderProps<T>) => children(props);

  // Stable references so the React.memo on HorizontalCardView actually holds.
  // A fresh `Object.keys(...)` array / inline `() => {}` on every render busts
  // memoization and re-renders the entire card grid mid-scroll, which is the
  // jank + white-gap users see in cards view.
  const cardVisibleColumns = React.useMemo(
    () => Object.keys(config.fields || {}),
    [config.fields]
  );
  const noopDelete = React.useCallback(() => {}, []);

  if (viewMode === "board" && BoardViewComponent) {
    return (
      <>
        {renderChildren({
          processedData: [],
          groupingContext,
          groupableFields,
          isCardView: false,
          hideTableBody: true,
        })}
        <BoardViewComponent />
      </>
    );
  }

  if (viewMode === "cards") {
    return (
      <>
        {renderChildren({
          processedData: [],
          groupingContext,
          groupableFields,
          isCardView: true,
          hideTableBody: true,
        })}
        <HorizontalCardView<T>
          config={config}
          data={data}
          visibleColumns={cardVisibleColumns}
          onView={onView}
          onEdit={onEdit}
          onDelete={noopDelete}
          appUser={appUser}
          onOpenDetailModal={onOpenDetailModal}
          hooks={hooks}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={onLoadMore}
          inlineEditingDisabled
        />
      </>
    );
  }

  // "List" view (the view-mode toggle's List button). A real, flat, dense list
  // of rows — collapsible group sections only when a group-by is active. No
  // longer gated on a group-by or a per-entity renderer; the row is generic.
  if (viewMode === "grouped") {
    return (
      <>
        {renderChildren({
          processedData: [],
          groupingContext,
          groupableFields,
          isCardView: false,
          hideTableBody: true,
        })}
        <GroupedListView<T>
          data={data}
          config={config}
          groupByField={groupByField || dualGrouping.primary}
          secondaryGroupByField={dualGrouping.secondary}
          hooks={hooks}
          appUser={appUser}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          onExport={onExport}
          onEdit={onEdit}
          onView={onView}
          onOpenDetailModal={onOpenDetailModal}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={onLoadMore}
        />
      </>
    );
  }

  return renderChildren({
    processedData,
    groupingContext,
    groupableFields,
    isCardView: false,
  });
}
